import { FC, useEffect, useState } from "react";

import { Alert, Button, Input, Modal, Space } from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";

import styles from "./SampleTypeItem.module.scss";

import { ISampleType } from "@/models/SampleType";
import useToggle from "@/hooks/useToggle";
import { SampleTypesApi } from "@/api";
import { ERROR_500_MESSAGE } from "@/api/constants";

const { confirm } = Modal;

interface SampleTypeProps {
  sampleType: ISampleType;
  editSampleType: (id: number, type: string) => void;
  deleteSampleType: (id: number) => void;
}

const SampleTypeItem: FC<SampleTypeProps> = ({
  sampleType,
  editSampleType,
  deleteSampleType,
}) => {
  const [edit, toggleEdit] = useToggle(false);
  const [newValue, setNewValue] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState("");

  useEffect(() => {
    setNewValue(sampleType.type);
  }, [sampleType]);

  const removeSampleType = async () => {
    try {
      setDeleteError("");
      setDeleteLoading(true);
      await SampleTypesApi.deleteSampleType(sampleType.sampleTypeId);
      deleteSampleType(sampleType.sampleTypeId);
    } catch (e: any) {
      setDeleteError(e.message || ERROR_500_MESSAGE);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDelete = () => {
    confirm({
      title: "Are you sure you want to delete this sample type?",
      icon: <ExclamationCircleOutlined />,
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk() {
        removeSampleType();
      },
    });
  };

  const handleEdit = async () => {
    try {
      setEditError("");
      setEditLoading(true);
      const { sampleTypeId, type } = await SampleTypesApi.editSampleType(
        sampleType.sampleTypeId,
        newValue
      );
      editSampleType(sampleTypeId, type);
      toggleEdit();
      setNewValue("");
    } catch (e: any) {
      setEditError(e.message || ERROR_500_MESSAGE);
    } finally {
      setEditLoading(false);
    }
  };

  return (
    <Space direction="vertical" className={styles.container}>
      <div className={styles.item}>
        {edit ? (
          <Input.Group compact>
            <Input
              style={{ width: "fit-content" }}
              disabled={editLoading}
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
            />
            <Button
              disabled={editLoading}
              loading={editLoading}
              type="primary"
              onClick={handleEdit}
            >
              Save
            </Button>
          </Input.Group>
        ) : (
          sampleType.type
        )}
        <Space>
          <Button
            data-testid="edit-sample-type"
            disabled={editLoading}
            icon={<EditOutlined />}
            onClick={toggleEdit}
          />
          <Button
            data-testid="delete-sample-type"
            disabled={deleteLoading}
            loading={deleteLoading}
            icon={<DeleteOutlined />}
            onClick={handleDelete}
          />
        </Space>
      </div>
      {deleteError && <Alert type="error" description={deleteError} />}
      {editError && <Alert type="error" description={editError} />}
    </Space>
  );
};

export default SampleTypeItem;
